import { useEffect, useState, useMemo, useCallback } from "react";
import { Col } from "reactstrap";
import { useMediaQuery } from "react-responsive";
import { APIMS } from "../../../../../config/apims";
import "bulma/css/bulma.css";
import "../../../../../index.css";
import Swal from "sweetalert2";
import ModalAdd from "./modal/add";
import ModalUpdate from "./modal/update";
import { useLocation, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faCodeBranch,
  faPenToSquare,
  faTrash,
  faMagnifyingGlass,
} from "@fortawesome/free-solid-svg-icons";
import { PulseLoader } from "react-spinners";
import ToastError from "../../../../NotificationToast/ToastError";
import ToastSuccess from "../../../../NotificationToast/ToastSuccess";
import Pagination from "../../../../components/pagination_new";

export default function Cabang() {
  document.title = "Cabang";

  const [getData, setGetData] = useState([]);
  const ascending = 0;
  const token = localStorage.getItem("token");

  const isTabletOrMobile = useMediaQuery({ query: "(max-width: 780px)" });

  const [searchKeyword, setSearchKeyword] = useState("");
  const [tablePage, setTablePage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isFetching, setIsFetching] = useState(false);

  const [id, setId] = useState();
  const [branchCodeUpdate, setBranchCodeUpdate] = useState();
  const [branchNameUpdate, setBranchNameUpdate] = useState();
  const [parentIdUpdate, setParentIdUpdate] = useState();

  const [modalAdd, setModalAdd] = useState(false);
  const [modalUpdate, setModalUpdate] = useState(false);

  const location = useLocation();
  const [permission, setPermission] = useState({
    create: 0,
    read: 0,
    update: 0,
    delete: 0,
  });

  const navigate = useNavigate();

  useEffect(() => {
    const storageLevel = localStorage.getItem("level");
    const storageItems = JSON.parse(localStorage.getItem("role_permission")) || [];

    if (storageLevel === "developer" || storageLevel === "superadmin") {
      setPermission({
        create: 1,
        read: 1,
        update: 1,
        delete: 1,
      });
    } else {
      const foundPermission = storageItems.find(
        (item) => item.menu?.url === location.pathname
      );

      if (!foundPermission) {
        navigate("/dashboard");
      } else {
        setPermission({
          create: foundPermission.create,
          read: foundPermission.read,
          update: foundPermission.update,
          delete: foundPermission.delete,
        });
      }
    }
  }, [location.pathname, navigate]);

  const fetchParams = useMemo(
    () => ({
      headers: {
        Authorization: `${token}`,
        "Content-Type": "application/json",
      },
    }),
    [token]
  );

  const GetResponseData = useCallback(async () => {
    setSearchKeyword("");
    setTablePage(1);
    setIsFetching(true);

    try {
      const response = await APIMS.get(
        `/api/branches?page=1&limit=1000&ascending=${ascending}&search=`,
        fetchParams
      );

      if (response?.status === 200) {
        setGetData(response?.data?.data || []);
      }
    } catch (error) {
      ToastError.fire({
        icon: "error",
        title: error.response?.data?.message || "Gagal memuat data",
      });
    } finally {
      setIsFetching(false);
    }
  }, [ascending, fetchParams]);

  useEffect(() => {
    GetResponseData();
  }, [GetResponseData]);

  const deleteById = async (id, name) => {
    Swal.fire({
      title: "Hapus data ini?",
      html: `<p class="sims-delete-description">Data <strong></strong> yang dihapus tidak dapat dikembalikan.</p>`,
      icon: "warning",
      customClass: {
        popup: "sims-delete-popup",
        icon: "sims-delete-icon",
        title: "sims-delete-title",
        actions: "sims-delete-actions",
        confirmButton: "sims-delete-confirm",
        cancelButton: "sims-delete-cancel",
        validationMessage: "sims-delete-validation",
      },
      buttonsStyling: false,
      showCancelButton: true,
      confirmButtonText: "Hapus data",
      cancelButtonText: "Batal",
      reverseButtons: true,
      focusCancel: true,

      didOpen: (popup) => {
        popup.querySelector(".sims-delete-description strong").textContent = name || "Cabang";
      },
      showLoaderOnConfirm: true,
      allowOutsideClick: () => !Swal.isLoading(),
      preConfirm: async () => {
        try {
          const response = await APIMS.delete(
            `/api/branches/${id}`,
            fetchParams
          );
  
          if (response.data.error) {
            throw new Error(response.data.message);
          }
  
          return response;
        } catch (error) {
          Swal.showValidationMessage(
            error.response?.data?.message || "Gagal menghapus data"
          );
        }
      },
    }).then((result) => {
      if (result.isConfirmed) {
        ToastSuccess.fire({
          icon: "success",
          title: "Data berhasil dihapus",
        });
        GetResponseData();
      }
    });
  };

  const viewModalUpdate = (branchId, branchCode, branchName, parentId) => {
    setId(branchId);
    setBranchCodeUpdate(branchCode);
    setBranchNameUpdate(branchName);
    setParentIdUpdate(parentId);
    setModalUpdate(true);
  };

  const nameCollator = useMemo(
    () => new Intl.Collator("id", { sensitivity: "base", numeric: true }),
    []
  );

  const filteredData = useMemo(() => {
    const keyword = (searchKeyword || "").trim().toLowerCase();

    const result = !keyword
      ? getData || []
      : (getData || []).filter((item) => {
          const kodeCabang = (item?.branch_initial || "")
            .toString()
            .toLowerCase();
          const namaCabang = (item?.branch_name || "")
            .toString()
            .toLowerCase();
          const branchCode = (item?.branch_code || "")
            .toString()
            .toLowerCase();

          return (
            kodeCabang.includes(keyword) ||
            namaCabang.includes(keyword) ||
            branchCode.includes(keyword)
          );
        });

    return [...result].sort((a, b) =>
      nameCollator.compare(
        (a?.branch_name || "").toString().trim(),
        (b?.branch_name || "").toString().trim()
      )
    );
  }, [getData, searchKeyword, nameCollator]);

  const totalPages = Math.max(1, Math.ceil(filteredData.length / rowsPerPage));

  useEffect(() => {
    setTablePage((page) => Math.min(page, totalPages));
  }, [totalPages]);

  const paginatedData = useMemo(() => {
    const safeRows = Math.max(1, rowsPerPage);
    const start = (tablePage - 1) * safeRows;
    return filteredData.slice(start, start + safeRows);
  }, [filteredData, tablePage, rowsPerPage]);

  return (
    <div className="body sims-page">
      {modalAdd && (
        <ModalAdd
          GetResponseData={GetResponseData}
          show={modalAdd}
          onHide={() => setModalAdd(false)}
        />
      )}

      {modalUpdate && (
        <ModalUpdate
          GetResponseData={GetResponseData}
          branchCodeUpdate={branchCodeUpdate}
          branchNameUpdate={branchNameUpdate}
          parentIdUpdate={parentIdUpdate}
          id={id}
          show={modalUpdate}
          onHide={() => setModalUpdate(false)}
        />
      )}

      <div className="page-header-card">
        <div className="page-header-top d-flex">
          <div className="page-title-wrap">
            <span className="page-title-icon">
              <FontAwesomeIcon icon={faCodeBranch} />
            </span>
            <h4 className="page-title-text">Data Cabang</h4>
          </div>

          <div className="ml-auto">
            <button
              onClick={() => setModalAdd(true)}
              className="btn btn-create mr-2 sims-header-button"
            >
              <FontAwesomeIcon icon={faPlus} />
              <span>{isTabletOrMobile ? "Cabang" : "Tambah Cabang"}</span>
            </button>
          </div>
        </div>
      </div>

      <div className="body-content sims-content">
        {isFetching ? (
          <div className="sims-loading">
            <PulseLoader color="#3f5bea" size={12} />
          </div>
        ) : (
          <Col xl="12" sm="12" className="sims-column">
            <div className="sims-search-wrap mb-3">
                <FontAwesomeIcon
                  icon={faMagnifyingGlass}
                  className="sims-search-icon"
                />
                <input
                  className="sims-search-input"
                  placeholder="Cari cabang..."
                  value={searchKeyword}
                  onChange={(event) => {
                    setSearchKeyword(event.target.value);
                    setTablePage(1);
                  }}
                />
              </div>

            <div className="sims-table-card">
              <div className="sims-table-scroll">
                <table className="sims-table">
                  <thead>
                    <tr>
                      <th className="sims-col-number">No</th>
                      <th style={{width: "200px"}}>Kode Cabang</th>
                      <th>Nama Cabang</th>
                      <th className="sims-col-action">Aksi</th>
                      
                    </tr>
                  </thead>

                  <tbody>
                    {paginatedData.length > 0 ? (
                      paginatedData.map((row, index) => (
                        <tr key={row?.id || index}>
                          <td className="sims-col-number">
                            <span className="sims-number-badge">
                              {(tablePage - 1) * rowsPerPage + index + 1}
                            </span>
                          </td>

                          <td>
                            <span className="sims-text-bold">
                              {row.branch_initial || row.branch_code || "-"}
                            </span>
                          </td>

                          <td>
                            <span className="sims-name-text">
                              {row.branch_name || "-"}
                            </span>
                          </td>

                          <td className="sims-col-action">
                            <div className="sims-action-wrap">
                              <button
                                type="button"
                                className="sims-action-button sims-action-edit"
                                title="Edit cabang"
                                onClick={() =>
                                  viewModalUpdate(
                                    row?.id,
                                    row?.branch_code,
                                    row?.branch_name,
                                    row?.parent_id
                                  )
                                }
                              >
                                <FontAwesomeIcon icon={faPenToSquare} />
                              </button>

                              <button
                                type="button"
                                className="sims-action-button sims-action-delete"
                                title="Hapus cabang"
                                onClick={() => deleteById(row?.id, row?.branch_name)}
                              >
                                <FontAwesomeIcon icon={faTrash} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="4" className="sims-empty-state">
                           <div className="module-empty-state-content">
                             Data cabang tidak ditemukan
                           </div>
                         
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <Pagination
                totalRows={filteredData.length}
                tablePage={tablePage}
                rowsPerPage={rowsPerPage}
                onPageChange={setTablePage}
                onRowsPerPageChange={(value) => {
                  setRowsPerPage(value);
                  setTablePage(1);
                }}
              />
            </div>
          </Col>
        )}
      </div>
    </div>
  );
}
